import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Check, User } from "lucide-react";
import { Logo } from "./Logo";
import { useI18n } from "@/i18n/I18nProvider";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

function useScrolled() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return scrolled;
}

export function Navbar() {
  const { lang, setLang, t } = useI18n();
  const scrolled = useScrolled();
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const links = [
    { to: "/", label: t.nav.home },
    { to: "/about", label: t.nav.about },
    { to: "/services", label: t.nav.services },
    { to: "/#clients", label: t.nav.portfolio },
    { to: "/technology", label: t.nav.technology },
    { to: "/contact", label: t.nav.contact },
  ];

  return (
    <>
      <header
        className={cn(
          "fixed inset-x-0 top-0 z-40 transition-all duration-500",
          scrolled
            ? "bg-background/70 backdrop-blur-xl border-b hairline"
            : "bg-transparent border-b border-transparent",
        )}
      >
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-6 lg:px-10">
          <Logo />

          <nav className="hidden lg:flex items-center gap-1">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className={cn(
                  "relative rounded-full px-4 py-2 text-sm transition-colors",
                  pathname === l.to
                    ? "text-foreground"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {pathname === l.to && (
                  <span className="absolute inset-0 rounded-full bg-foreground/[0.06]" />
                )}
                <span className="relative">{l.label}</span>
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-1.5">
            <DropdownMenu>
              <DropdownMenuTrigger
                className="hidden sm:inline-flex h-9 items-center gap-1.5 rounded-full border hairline bg-background/40 px-3 text-xs font-medium uppercase tracking-wider text-muted-foreground transition-colors hover:text-foreground hover:bg-foreground/[0.04]"
                aria-label={t.nav.language}
              >
                {lang.toUpperCase()}
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[8rem]">
                {(["en", "es"] as const).map((l) => (
                  <DropdownMenuItem
                    key={l}
                    onClick={() => setLang(l)}
                    className="flex items-center justify-between"
                  >
                    <span>{l === "en" ? "English" : "Español"}</span>
                    {lang === l && <Check className="h-3.5 w-3.5" />}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <Link
              to="/contact"
              className="hidden lg:inline-flex h-9 items-center rounded-full bg-foreground px-4 text-sm font-medium text-background transition-opacity hover:opacity-90"
            >
              {t.cta.contact}
            </Link>

            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Link
                    to="/os/select"
                    aria-label="Smart Canvas OS"
                    className="hidden sm:inline-flex h-9 w-9 items-center justify-center rounded-full border hairline bg-background/40 text-muted-foreground transition-colors hover:text-foreground hover:bg-foreground/[0.04]"
                  >
                    <User className="h-4 w-4" />
                  </Link>
                </TooltipTrigger>
                <TooltipContent side="bottom" sideOffset={6}>
                  <p className="text-xs">{t.nav.osLoginTooltip}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <button
              onClick={() => setOpen(true)}
              className="lg:hidden inline-flex h-9 w-9 items-center justify-center rounded-full border hairline"
              aria-label={t.nav.menu}
            >
              <Menu className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      <div
        className={cn(
          "fixed inset-0 z-50 lg:hidden",
          open ? "pointer-events-auto" : "pointer-events-none",
        )}
        aria-hidden={!open}
      >
        <div
          onClick={() => setOpen(false)}
          className={cn(
            "absolute inset-0 bg-background/60 backdrop-blur-md transition-opacity duration-500",
            open ? "opacity-100" : "opacity-0",
          )}
        />
        <aside
          className={cn(
            "absolute right-0 top-0 h-full w-[88%] max-w-sm bg-background border-l hairline shadow-2xl flex flex-col transition-transform duration-500 [transition-timing-function:var(--ease-out-quart)]",
            open ? "translate-x-0" : "translate-x-full",
          )}
        >
          <div className="flex h-16 items-center justify-between px-6 border-b hairline">
            <Logo />
            <button
              onClick={() => setOpen(false)}
              aria-label={t.nav.close}
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border hairline"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <nav className="flex-1 overflow-y-auto px-6 py-8">
            <ul className="space-y-1">
              {links.map((l, i) => (
                <li
                  key={l.to}
                  className="animate-fade-up"
                  style={{ animationDelay: `${i * 60}ms` }}
                >
                  <Link
                    to={l.to}
                    onClick={() => setOpen(false)}
                    className={cn(
                      "group flex items-center justify-between rounded-2xl px-4 py-4 text-2xl font-display tracking-tight transition-colors",
                      pathname === l.to
                        ? "bg-foreground/[0.06] text-foreground"
                        : "text-muted-foreground hover:text-foreground hover:bg-foreground/[0.03]",
                    )}
                  >
                    <span>{l.label}</span>
                    <span className="text-xs text-muted-foreground/60 tabular-nums">
                      0{i + 1}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
            <div className="mt-6 pt-6 border-t hairline">
              <Link
                to="/os/select"
                onClick={() => setOpen(false)}
                className="group flex items-center justify-between rounded-2xl px-4 py-4 text-base font-medium text-muted-foreground hover:text-foreground hover:bg-foreground/[0.03] transition-colors"
              >
                <span className="inline-flex items-center gap-3">
                  <User className="h-4 w-4" />
                  Smart Canvas OS
                </span>
                <span className="text-xs uppercase tracking-wider text-muted-foreground/60">
                  Login
                </span>
              </Link>
            </div>
          </nav>
          <div className="border-t hairline px-6 py-5 space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-xs uppercase tracking-wider text-muted-foreground">
                {t.nav.language}
              </span>
              <div className="inline-flex rounded-full border hairline p-0.5">
                {(["en", "es"] as const).map((l) => (
                  <button
                    key={l}
                    onClick={() => setLang(l)}
                    className={cn(
                      "px-3 py-1 rounded-full text-xs font-medium uppercase tracking-wider transition-colors",
                      lang === l ? "bg-foreground text-background" : "text-muted-foreground",
                    )}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </div>
            <Link
              to="/contact"
              onClick={() => setOpen(false)}
              className="flex h-12 items-center justify-center rounded-full bg-foreground text-background text-sm font-medium"
            >
              {t.cta.contact}
            </Link>
          </div>
        </aside>
      </div>
    </>
  );
}